import React from 'react';
import { createMuiTheme, MuiThemeProvider as MuiThemeProvider$1 } from '@material-ui/core';
import verticalRhythm from 'compass-vertical-rhythm';
import gray from 'gray-percentage';
import { transparentize } from 'polished';

function modularscale(value, ratio) {
    return Math.pow(ratio, value);
}
function inset(size) {
    var pixelSize = "".concat(size, "px");
    return "".concat(pixelSize, " ").concat(pixelSize, " ").concat(pixelSize, " ").concat(pixelSize);
}
function stack(size) {
    var pixelSize = "".concat(size, "px");
    return "0 0 ".concat(pixelSize, " 0");
}
var sizeUnit = 4;
var sizes = {
    topBarHeight: 56,
    minViewportHeight: 500,
    maxSingleViewWidth: 1440,
    projectDrawerWidth: 240,
};
var zIndexes = { modal: 2000, tooltip: 2010, tooltipArrow: '-1' };
var typographyBaseline = {
    baseFontSize: '16px',
    baseLineHeight: 1.5,
    headerLineHeight: 1.1,
    scaleRatio: 2.25,
    bodyFontFamily: "'PT Sans', sans-serif",
    headerFontFamily: "'PT Sans', sans-serif",
    bodyWeight: 400,
    headerWeight: 500,
    headerColor: 'inherit',
    // bodyColor: gray(15, 301),
    bodyColor: '#162A4E',
};
var vr = verticalRhythm(typographyBaseline);
function scale(value) {
    var baseFont = parseInt(typographyBaseline.baseFontSize, 10);
    var newFontSize = "".concat(modularscale(value, typographyBaseline.scaleRatio) * baseFont, "px");
    return vr.adjustFontSizeTo(newFontSize);
}
var typographyCalc = {
    baseline: vr.establishBaseline(),
    h1: scale(4 / 4),
    h2: scale(3 / 4),
    h3: scale(3 / 5),
    h4: scale(1 / 6),
    h5: scale(-1 / 6),
    h6: scale(-2 / 6),
    caption: scale(-4 / 7),
};
var spacing = {
    inset: inset,
    stack: stack,
};
var shadows = {
    default: '0 0 20px 0 rgba(0,0,0,0.1)',
    soft: '0 0 20px 0 rgba(0,0,0,0.05)',
};
var gradients = {
    default: function (from, to) {
        return "radial-gradient(farthest-corner at ".concat(from, "px ").concat(to, "px,#61045f 0%,#282d4c 100%)");
    },
};
var white = '#fff';
// https://github.com/KyleAMathews/gray-percentage#gray-percentage
var colors = {
    primary: '#6663FD',
    accent: '#03C1DD',
    secondaryAccentDark: '#360e4f',
    secondaryAccentLight: '#bd30ff',
    background: '#F6FAFF',
    contentBg: '#FFFFFF',
    warning: '#FF9800',
    contrast: '#e21a85',
    text: typographyBaseline.bodyColor,
    inactiveLinkLight: gray(85, 301, true),
    inactiveLink: gray(50, 301),
    label: gray(50, 301),
    hoveredLink: gray(30, 301),
    userPillGray: '#E1E8F1',
    hoveredLinkLight: gray(100, 301, true),
    hovereredLightButtonBackground: '#F6F7FB',
    listBorder: '#F0F2FB',
    lightBlue: '#f2f5ff',
    tonedYellow: '#FFEB3B',
    flatCardBorder: '#9BB2D0',
    themeGray: '#E5E5E5',
    dragHandle: '#9BB2D0',
    highlightedItemBg: '#F7F9FB',
    grayBackground: '#8f8f8f4f',
    blueGray: '#7F8FA4',
    pillGray: 'rgba(155, 178, 208, 0.3)',
    error: '#E53935',
    themeBlue: '#639AE0',
    formInactiveBar: '#D5E1F0',
    listItemBg: '#F6FAFE',
    contentStatus: {
        draft: '#4A90E2',
        archived: '#9BB2D0',
    },
    webhookUrlBg: '#273A59',
    placeholder: {
        flasher: '#5e6cc3',
        bg: '#424b86',
    },
    newSidebarBg: '#162A4E',
    newSidebarContextHeading: '#1c2f53',
    newSidebarContextBg: '#253759',
    // newSidebarInnerNav: '#0A1E42',
    newSidebarInnerNav: '#1c2f53',
    newSidebarLoadMoreBg: '#0A1E42',
    dismissButton: '#CFDAE8',
    onboardingHeaderContent: '#f1f5ff',
    contentTableText: '#162A4E',
    contentFormBackground: '#fdfdff',
    contentFormInactiveBorder: '#f3f6fb',
    cardBody: '#f6faff',
    scrollbars: '#717D93',
    activeTab: white,
    inactiveTab: 'whitesmoke',
};
var styles = {
    paper: "\n    overflow: hidden;\n    background-color: ".concat(colors.contentBg, ";\n    border-radius: 5px;\n    box-shadow: ").concat(shadows.default, ";\n  "),
    focus: function (color) { return "\n    box-shadow: 0 0 0 3px ".concat(transparentize(0.5, color), ";\n    animation: none;\n  "); },
};
var theme = {
    typographyBaseline: typographyBaseline,
    typographyCalc: typographyCalc,
    styles: styles,
    sizeUnit: sizeUnit,
    colors: colors,
    shadows: shadows,
    gradients: gradients,
    spacing: spacing,
    sizes: sizes,
    zIndexes: zIndexes,
};

var makeMuiTheme = (function (theme) { return ({
    overrides: {
        MuiPaper: {
            // Name of the component ⚛️ / style shee
            rounded: {
                // Name of the rule
                borderRadius: '5px',
            },
        },
    },
    typography: {
        fontFamily: theme.typographyBaseline.bodyFontFamily,
        fontSize: parseInt(theme.typographyBaseline.baseFontSize, 10),
        fontWeightLight: theme.typographyBaseline.bodyWeight,
        fontWeightRegular: theme.typographyBaseline.bodyWeight,
        fontWeightMedium: theme.typographyBaseline.headerWeight,
    },
    palette: {
        primary: { main: theme.colors.primary },
        secondary: { main: theme.colors.accent },
        background: {
            paper: theme.colors.contentBg,
            default: theme.colors.background,
        },
    },
    zIndex: {
        modal: theme.zIndexes.modal,
        tooltip: theme.zIndexes.tooltip,
        tooltipArrow: theme.zIndexes.tooltipArrow,
    },
}); });

function MuiThemeProvider(props) {
    var _a = props.children, children = _a === void 0 ? null : _a;
    var createdMuiTheme = createMuiTheme(makeMuiTheme(theme));
    return (React.createElement(MuiThemeProvider$1, { theme: createdMuiTheme }, children));
}

export { MuiThemeProvider };
//# sourceMappingURL=index.esm.js.map
